import { useTranslation } from 'react-i18next'

import { MinusIcon, PlusIcon } from 'app/common/presentation/icons'
import { ConnectionName, SupportedAccount } from 'app/connections/types'

import { Button, CodeTag, IconButton, Table } from 'components'

import { maskValue } from '../../../maskValue'
import { SettingsSection } from '../Section/Section'
import styles from './Accounts.module.scss'

interface AccountsSettingsProps<T extends SupportedAccount> {
  accounts: T[]
  connectionName: ConnectionName
  onAdd?: () => void
  onRemove: (account: T) => void
}

const getAccountId = (account: SupportedAccount) =>
  'externalId' in account && typeof account.externalId === 'string'
    ? account.externalId
    : account.id

export const AccountsSettings = <T extends SupportedAccount>({
  accounts,
  connectionName,
  onAdd,
  onRemove,
}: AccountsSettingsProps<T>) => {
  const { t } = useTranslation('connections')

  return (
    <SettingsSection
      contentClassName={styles.accountsContent}
      description={t('manage.accounts.description')}
      title={t('manage.accounts.title')}
    >
      <Table.Table
        className={styles.accountsTable}
        columns={[
          t(`setup.${connectionName}.account.id`),
          t(`setup.${connectionName}.account.name`),
          <span aria-label={t('manage.accounts.actions')} key="actions" />,
        ]}
        data={accounts}
        renderRow={(account) => (
          <Table.Row key={account.id}>
            <Table.Cell>
              <CodeTag>{maskValue(getAccountId(account))}</CodeTag>
            </Table.Cell>
            <Table.Cell>{account.name}</Table.Cell>
            <Table.Cell className={styles.accountActions}>
              <IconButton
                aria-label={t('manage.accounts.remove', { name: account.name })}
                icon={MinusIcon}
                onClick={() => onRemove(account)}
                small
                type="button"
                variant="tertiary"
              />
            </Table.Cell>
          </Table.Row>
        )}
      />
      <div className={styles.accountsFooter}>
        <Button
          disabled={!onAdd}
          onClick={onAdd}
          rightIcon={<PlusIcon />}
          small
          type="button"
          variant="secondary"
        >
          {t('manage.accounts.add')}
        </Button>
      </div>
    </SettingsSection>
  )
}
